import { useTranslation } from 'react-i18next';
import { Calendar, CheckCircle2 } from 'lucide-react';
import type { Employee } from '@momentum/types';
import { cn } from '@/shared/utils/utils';
import { useDateFormatter } from '@/shared/hooks';
import { OutcomeResult } from '../../describe-stage/types/describe.types';
import { ParticipantAvatar } from './ParticipantAvatar';

interface LessonsHeaderProps {
  goalDescription: string;
  conductedOn: Date;
  outcome: OutcomeResult;
  participants: Employee[];
}

export function LessonsHeader({
  goalDescription,
  conductedOn,
  outcome,
  participants,
}: LessonsHeaderProps) {
  const { t } = useTranslation('debriefV2');
  const { formatDate } = useDateFormatter();

  const isAchieved = outcome === 'achieved';
  const outcomeLabel = isAchieved
    ? t('lessons.outcomeAchieved')
    : t('lessons.outcomeNotAchieved');

  return (
    <header className="w-full px-8 pt-6 pb-5 bg-white border-b border-slate-200 shrink-0 flex flex-col gap-3">
      <div className="flex items-center justify-end w-full">
        <span
          className={cn(
            'px-3 py-1 rounded-md text-xs font-semibold flex items-center gap-1.5 uppercase tracking-wider shadow-sm border',
            isAchieved
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : 'bg-amber-50 text-amber-700 border-amber-200'
          )}
        >
          <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
          {outcomeLabel}
        </span>
      </div>
      <div className="flex items-end justify-between w-full gap-6 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 break-words">
            {goalDescription}
          </h1>
          <p className="text-slate-500 text-sm mt-1.5 flex items-center gap-2">
            <Calendar className="h-4 w-4 shrink-0" aria-hidden />
            {formatDate(conductedOn, 'medium')}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <span className="text-[10px] uppercase font-bold tracking-widest text-slate-400">
            {t('lessons.participantsCount')(participants.length)}
          </span>
          <div className="flex -space-x-2 rtl:space-x-reverse">
            {participants.map((employee) => (
              <ParticipantAvatar
                key={employee._id}
                employee={employee}
                size="md"
              />
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}
